// src/components/inventory/InventoryProductFormModal.tsx
// Scheda di un prodotto in un modale: si apre sia quando la scansione trova un
// codice mai visto (gli si dà un nome e entra a magazzino) sia quando si preme
// "Modifica" su un prodotto già in elenco.
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { PackagePlus, Megaphone, Loader2, Plus, Check } from 'lucide-react';

import Modal from '@/components/ui/Modal';
import CustomSelect from '@/components/ui/CustomSelect';
import QuantityInput from '@/components/ui/QuantityInput';
import { useInventory } from '@/contexts/InventoryContext';
import { PRODUCT_CATEGORIES, InventoryProduct } from '@/services/inventory/inventoryService';

// Stessa altezza di CustomSelect e QuantityInput, così la riga resta allineata.
const fieldBase =
    'w-full bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-clip-200 focus:border-clip-400 outline-none font-medium transition-all placeholder:text-gray-400 text-gray-900';
const inputCls = `${fieldBase} px-3.5 py-3`;
const priceInputCls = `${fieldBase} pl-9 pr-3.5 py-3`;
const labelCls = 'block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2';

interface InventoryProductFormModalProps {
    isOpen: boolean;
    onClose: () => void;
    /** Codice appena scansionato: presente solo quando si crea un prodotto nuovo. */
    barcode?: string | null;
    /** Prodotto da modificare: presente solo in modifica. */
    product?: InventoryProduct | null;
    /** Chiamata dopo la creazione, con l'id del prodotto appena entrato a magazzino. */
    onCreated?: (productId: number) => void;
}

const InventoryProductFormModal: React.FC<InventoryProductFormModalProps> = ({
    isOpen,
    onClose,
    barcode = null,
    product = null,
    onCreated,
}) => {
    const { t } = useTranslation();
    const { addProduct, addMovement, editProduct } = useInventory();
    const isEdit = !!product;

    const [name, setName] = useState('');
    const [category, setCategory] = useState<string>(PRODUCT_CATEGORIES[0]);
    const [price, setPrice] = useState('');
    const [threshold, setThreshold] = useState('');
    const [quantity, setQuantity] = useState(1);
    const [isSaving, setIsSaving] = useState(false);

    // Reset (o precompilazione in modifica) a ogni apertura.
    useEffect(() => {
        if (!isOpen) return;
        setName(product?.name ?? '');
        setCategory(product?.category ?? PRODUCT_CATEGORIES[0]);
        setPrice(product ? String(product.price ?? '') : '');
        setThreshold(product ? String(product.min_threshold ?? 0) : '');
        setQuantity(1);
        setIsSaving(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, product?.id]);

    const canSubmit = name.trim().length > 0 && !isSaving;

    const handleCreate = async () => {
        setIsSaving(true);
        const created = await addProduct({
            name: name.trim(),
            category,
            barcode,
            price: Number(price) || 0,
        });
        if (!created.success || !created.data) {
            setIsSaving(false);
            toast.error(created.error || t('common.error'));
            return;
        }
        const res = await addMovement({ product_id: created.data.id, type: 'in', quantity });
        setIsSaving(false);
        if (!res.success) {
            toast.error(res.error || t('common.error'));
            return;
        }
        toast.success(t('inventory.scan.product_created', 'Prodotto creato ed entrato a magazzino'));
        onClose();
        onCreated?.(created.data.id);
    };

    const handleSave = async () => {
        if (!product) return;
        setIsSaving(true);
        const res = await editProduct(product.id, {
            name: name.trim() || product.name,
            category,
            price: Number(price) || 0,
            min_threshold: Number(threshold) || 0,
        });
        setIsSaving(false);
        if (res.success) {
            toast.success(t('common.save_success', 'Modifiche salvate'));
            onClose();
        } else {
            toast.error(res.error || t('common.error'));
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            className="max-w-xl"
            headerClassName="bg-white border-b border-gray-100"
            closeButtonClassName="bg-gray-100 hover:bg-gray-200 text-gray-500"
            headerContent={
                <div className="flex items-center gap-3.5 pr-10">
                    <div className="w-12 h-12 rounded-2xl bg-clip-50 text-clip-600 flex items-center justify-center flex-shrink-0">
                        <PackagePlus size={22} />
                    </div>
                    <div className="min-w-0">
                        <h2 className="text-xl font-bold text-gray-900 truncate">
                            {isEdit
                                ? t('inventory.edit_product', 'Modifica prodotto')
                                : t('inventory.new_product', 'Nuovo prodotto')}
                        </h2>
                        <p className="text-sm text-gray-400 truncate">
                            {isEdit
                                ? t('inventory.edit_subtitle', 'Nome, categoria, prezzo e soglia minima.')
                                : t('inventory.new_product_subtitle', 'Dagli un nome e falla entrare a magazzino.')}
                        </p>
                    </div>
                </div>
            }
        >
            <div className="space-y-6">
                {/* L'avviso vive in un box, non come riga di testo sciolta. */}
                {!isEdit && (
                    <div className="flex items-start gap-3 p-4 rounded-2xl bg-clip-50 border border-clip-100">
                        <div className="w-9 h-9 rounded-xl bg-white/70 text-clip-600 flex items-center justify-center flex-shrink-0">
                            <Megaphone size={16} />
                        </div>
                        <div className="min-w-0 space-y-1.5 pt-0.5">
                            <p className="text-sm font-bold text-clip-700 leading-snug">
                                {t(
                                    'inventory.scan.new_code',
                                    'Codice nuovo in ingresso — dagli un nome (solo la prima volta)'
                                )}
                            </p>
                            <div className="flex items-center gap-2 flex-wrap text-xs text-clip-600/90">
                                <span>{t('inventory.scan.barcode', 'Codice a barre')}</span>
                                <span className="px-2 py-0.5 rounded-md bg-white/70 border border-clip-100 font-mono font-bold text-clip-700 tracking-wide">
                                    {barcode}
                                </span>
                            </div>
                        </div>
                    </div>
                )}

                <div>
                    <label className={labelCls}>{t('inventory.product_name', 'Nome prodotto')}</label>
                    <input
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        placeholder={t('inventory.product_name_placeholder', 'es. Tinta 7.3 Biondo Dorato') as string}
                        className={inputCls}
                    />
                </div>

                <div className="relative z-30">
                    <label className={labelCls}>{t('inventory.category', 'Categoria')}</label>
                    <CustomSelect
                        value={category}
                        onChange={setCategory}
                        options={PRODUCT_CATEGORIES.map((c) => ({ value: c, label: c }))}
                    />
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-6">
                    <div>
                        <label className={labelCls}>{t('inventory.price', 'Prezzo')}</label>
                        <div className="relative">
                            <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 font-medium pointer-events-none">
                                €
                            </span>
                            <input
                                type="number"
                                min="0"
                                step="0.01"
                                inputMode="decimal"
                                value={price}
                                onChange={(e) => setPrice(e.target.value)}
                                placeholder="0,00"
                                className={priceInputCls}
                            />
                        </div>
                    </div>

                    {isEdit ? (
                        <div>
                            <label className={labelCls}>{t('inventory.min_threshold', 'Soglia minima')}</label>
                            <input
                                type="number"
                                min="0"
                                inputMode="numeric"
                                value={threshold}
                                onChange={(e) => setThreshold(e.target.value)}
                                className={inputCls}
                            />
                        </div>
                    ) : (
                        <div>
                            <label className={labelCls}>
                                {t('inventory.scan.qty_in', 'Quantità in ingresso')}
                            </label>
                            <QuantityInput value={quantity} onChange={setQuantity} disabled={isSaving} />
                        </div>
                    )}
                </div>

                <div className="flex justify-end pt-5 border-t border-gray-100">
                    <button
                        type="button"
                        onClick={isEdit ? handleSave : handleCreate}
                        disabled={!canSubmit}
                        className="flex items-center gap-2 px-6 py-3 bg-clip-500 hover:bg-clip-600 text-white font-bold rounded-xl text-sm shadow-sm shadow-clip-200 transition-all active:scale-95 disabled:opacity-50 disabled:active:scale-100"
                    >
                        {isSaving ? (
                            <Loader2 size={16} className="animate-spin" />
                        ) : isEdit ? (
                            <Check size={16} />
                        ) : (
                            <Plus size={16} />
                        )}
                        {isEdit ? t('inventory.save_changes', 'Salva modifiche') : t('common.add', 'Aggiungi')}
                    </button>
                </div>
            </div>
        </Modal>
    );
};

export default InventoryProductFormModal;
